/**
 * The first widgets that may hold a NON-STREAMING Stage.
 *
 * DELIBERATELY NARROW. `PRODUCT_PLAN.md` D10 is unresolved: in the one anchor
 * reviewed closely, the widget types that could plausibly fill a non-streaming
 * Stage — Playable ad, Journey Engine content, Leaderboard — are shown
 * competing for the COLUMN, not the Stage. Whether the Stage draws from that
 * same catalogue or a narrower one is an open question, and building the full
 * catalogue now would answer it by accident. So: two purpose-built occupants,
 * and a real second adopter (E.2) before anything is generalised.
 *
 * ── WHY THE BRAND PANEL IS A WIDGET AND NOT SURFACESTAGE'S IDLE BRANCH ──
 *
 * `SurfaceStage` renders the anchor's resting content — lockup, phase line,
 * synopsis, CTAs, stat strip, co-streaming pile — from its own props, as a
 * hard-coded branch. That is right for HUB, whose anchor has exactly that
 * content, and it stays: this file does not move it.
 *
 * What it could not do is travel. PAGE has no non-streaming Stage concept at
 * all (§2.5b), and a host that wants one had no widget to mount — only a
 * component with the publisher anchor's fields baked into its signature. These
 * are that widget, so the class has at least one occupant any host can hold.
 */

import React from "react";
import {isSafeUrl} from "@/utils/isSafeUrl";
import "./stageWidgets.scss";

export interface StageBrandPanelProps {
    /** The surface's lockup. Falls back to type, never the other way round. */
    logoUrl?: string;
    name: string;
    /** e.g. "Launching Oct 23". */
    phaseLabel?: string;
    synopsis?: string;
    /** Primary then secondary. More than two crowds the band. */
    actions?: React.ReactNode;
}

/**
 * Key art, what this surface IS, and what to do about it.
 *
 * The anchor's own resting content, reduced to what generalises. The stat
 * strip and the co-streaming avatar pile are NOT here on purpose — a launch
 * countdown and a "co-streaming right now" pile are what the publisher anchor
 * happened to put in its Stage, and §2.5b is explicit that the anchor's
 * "Identity" label describes that one campaign's choice rather than the
 * category. A host that wants them still has them on `SurfaceStage`.
 */
export const StageBrandPanel: React.FC<StageBrandPanelProps> = ({
    logoUrl, name, phaseLabel, synopsis, actions,
}) =>
    <div className="sh-stage-brand">
        {/*
          * ORDER: the state line, then the identity, then the copy. The state
          * is what has CHANGED since the viewer was last here, which is why the
          * anchor leads with it; the identity is the same every visit.
          */}
        {phaseLabel &&
            <span className="sh-stage-brand__phase">
                <span className="sh-stage-brand__dot" aria-hidden="true" />
                {phaseLabel}
            </span>}

        {/*
          * Crest AND name on ONE LINE, not stacked: the mark and the name are
          * one lockup, and stacking them made the crest a second heading above
          * the first. With no crest the type stands alone.
          */}
        <span className="sh-stage-brand__id">
            {logoUrl && <img className="sh-stage-brand__logo" src={logoUrl} alt="" loading="lazy" />}
            <span className="sh-stage-brand__name">{name}</span>
        </span>
        {synopsis && <p className="sh-stage-brand__synopsis">{synopsis}</p>}
        {actions && <div className="sh-stage-brand__actions">{actions}</div>}
    </div>;

export interface StageAdPanelProps {
    imageUrl: string;
    /**
     * REQUIRED. An ad is the one occupant a viewer has not chosen, so it is
     * the one that most needs a text alternative — an unlabelled ad is a
     * decorative image to a screen reader and its message is simply lost.
     */
    alt: string;
    href?: string;
    /**
     * The advertiser. Rendered, not optional-in-practice: a paid placement a
     * viewer cannot tell is paid is the thing disclosure rules exist about,
     * and a Stage is the most prominent slot on the surface.
     */
    sponsorName: string;
    onClick?: () => void;
}

/**
 * A paid placement in the Stage.
 *
 * Always labelled. The disclosure is part of the widget rather than something
 * a host remembers to add, because a host that forgets produces an
 * undisclosed ad in the largest slot on the page.
 */
export const StageAdPanel: React.FC<StageAdPanelProps> = ({
    imageUrl, alt, href, sponsorName, onClick,
}) => {
    const art = <img className="sh-stage-ad__img" src={imageUrl} alt={alt} loading="lazy" />;
    /*
     * SCHEME ALLOW-LIST BEFORE THE HREF IS RENDERED.
     *
     * `href` is operator-supplied placement data. Without this a stored
     * `javascript:` URL executes in the visitor's page context the moment the
     * ad is clicked — the largest, most-clicked slot on the surface. An unsafe
     * value degrades to the un-linked art rather than throwing, because a
     * mis-configured ad should still render its disclosure.
     *
     * `isSafeUrl` is the shared allow-list (`utils/isSafeUrl.ts`), not a local
     * check — the comment on that file exists precisely so this rule cannot
     * drift between copies.
     */
    const safeHref = href && isSafeUrl(href) ? href : undefined;
    return (
        <div className="sh-stage-ad">
            {safeHref
                ? <a
                    className="sh-stage-ad__link"
                    href={safeHref}
                    onClick={onClick}
                    target="_blank"
                    /*
                     * `noopener` is a security requirement on any target=_blank
                     * to an address we do not control — without it the opened
                     * page gets a handle on ours through `window.opener`.
                     */
                    /* eslint-disable-next-line react/no-invalid-html-attribute -- `sponsored` is a standard rel value; the rule's allowlist predates it and this is a paid placement. */
                    rel="noopener noreferrer sponsored"
                >
                    {art}
                </a>
                : art}
            <span className="sh-stage-ad__disclosure">Sponsored · {sponsorName}</span>
        </div>
    );
};
